/**
 * useApprovalsList — list + filter the signed-in user's approvals.
 *
 * Wraps `fetchApprovals()` from graphApprovalsClient with abort + refresh
 * semantics. Returns mock data when VITE_FEATURE_APPROVALS_LIVE is off (so the
 * UI is fully exercised before AAD consent lands).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchApprovals,
  type ApprovalsListOptions,
  type GraphApproval,
} from '../services/graphApprovalsClient';

export interface UseApprovalsListResult {
  approvals: ReadonlyArray<GraphApproval>;
  loading: boolean;
  error: string | null;
  isLive: boolean;
  fallbackReason: string | null;
  refresh: () => void;
  /** Optimistic local mutation — used by useApprovalDecision after a POST. */
  patchApproval: (id: string, patch: Partial<GraphApproval>) => void;
  removeApproval: (id: string) => void;
}

export function useApprovalsList(opts: ApprovalsListOptions = {}): UseApprovalsListResult {
  const [approvals, setApprovals] = useState<ReadonlyArray<GraphApproval>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isLive, setIsLive] = useState<boolean>(false);
  const [fallbackReason, setFallbackReason] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const optsRef = useRef<ApprovalsListOptions>(opts);
  optsRef.current = opts;

  const refresh = useCallback((): void => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const result = await fetchApprovals({ ...optsRef.current, signal: controller.signal });
        if (controller.signal.aborted) return;
        setApprovals(result.approvals);
        setIsLive(result.isLive);
        setFallbackReason(result.reason ?? null);
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    refresh();
    return () => abortRef.current?.abort();
  }, [refresh]);

  const patchApproval = useCallback((id: string, patch: Partial<GraphApproval>): void => {
    setApprovals((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    );
  }, []);

  const removeApproval = useCallback((id: string): void => {
    setApprovals((prev) => prev.filter((a) => a.id !== id));
  }, []);

  return { approvals, loading, error, isLive, fallbackReason, refresh, patchApproval, removeApproval };
}
